import React from 'react'
import { Select as AntSelect } from 'antd'
import type { SelectProps as AntSelectProps } from 'antd'
import { Field } from '../Field/Field'
import type { RefSelectProps } from 'antd/es/select'
import type { FieldControlBag, FieldLabelling, FieldOwnProps, FieldSize } from '../Field/types'

export type SelectFieldMode = 'single' | 'multiple' | 'tags'
/** The library's one size vocabulary, shared with every other field. */
export type SelectFieldSize = FieldSize

export interface SelectFieldOption {
  label: string
  /**
   * Widened from `string` to also accept `number`/`null` - several real Selects
   * (month/day-of-month pickers, see docs/component-audit/selects.html) are
   * numeric and were kept as raw antd `Select` rather than coerced through strings.
   */
  value: string | number | null
  /**
   * Native tooltip, falling back to `label`, so an option repeats its visible text on hover. Left
   * that way on purpose: `title` is how the render and e2e suites select options. Pass `''` where
   * the repetition is noise.
   */
  title?: string
}

interface SelectFieldOwnProps
  extends
    Omit<AntSelectProps, 'mode' | 'size' | 'options' | 'loading' | 'showSearch' | 'allowClear' | 'status' | 'id' | 'aria-describedby' | 'aria-invalid' | 'aria-required'>,
    FieldOwnProps {
  /** @default 'single' */
  mode?: SelectFieldMode
  /** @default 'default' */
  size?: SelectFieldSize
  /**
   * Whether typing filters the option list. Defaults on; pass `false` for short fixed lists
   * (under roughly 8 options) where search adds nothing.
   * @default true
   */
  searchable?: boolean
  clearable?: boolean
  loading?: boolean
  options: SelectFieldOption[]
}

/** `label` or `ariaLabel`, never neither - a combobox without a name fails axe. */
export type SelectFieldProps = SelectFieldOwnProps & FieldLabelling

const MODE_TO_ANT: Record<SelectFieldMode, AntSelectProps['mode']> = {
  single: undefined,
  multiple: 'multiple',
  tags: 'tags',
}

const MULTI_MIN_WIDTH = 200

const SIZE_TO_ANT: Record<SelectFieldSize, AntSelectProps['size']> = {
  small: 'small',
  default: 'middle',
  large: 'large',
}

/**
 * The one Select. Its label, description, requirement marker and message come from `Field`.
 *
 * Pass `label` wherever there is room for one; a filter row, toolbar or table cell takes
 * `ariaLabel` instead. Width comes from the container, never the control - size the Stack row,
 * grid track or cell so a surface decides once rather than per select.
 */
export function SelectField({
  mode = 'single',
  size = 'default',
  searchable = true,
  clearable = false,
  loading = false,
  options,
  // Field-owned chrome, destructured so none of it reaches the antd control as an unknown prop.
  label,
  hideLabel,
  ariaLabel,
  description,
  tooltip,
  requirement,
  error,
  warning,
  name,
  rules,
  valuePropName,
  trigger,
  id,
  'data-qa': dataQaAttr,
  ...rest
}: SelectFieldProps) {
  // antd absolutely-positions the placeholder, so an empty multiple/tags select has no in-flow
  // content and collapses to the chevron. Floor it unless the caller sized it.
  const callerSized = rest.style !== undefined && ('width' in rest.style || 'minWidth' in rest.style)
  const style = mode !== 'single' && !callerSized ? { minWidth: MULTI_MIN_WIDTH, ...rest.style } : rest.style
  const field = {
    label,
    hideLabel,
    ariaLabel,
    description,
    tooltip,
    requirement,
    error,
    warning,
    name,
    rules,
    valuePropName,
    trigger,
    id,
    'data-qa': dataQaAttr,
    disabled: rest.disabled,
  }
  const antStatus: 'error' | 'warning' | undefined = error !== undefined ? 'error' : warning !== undefined ? 'warning' : undefined
  // rc-select echoes aria-required onto its roleless root div, which axe reports as a critical
  // aria-allowed-attr violation. Dropped after render; the combobox keeps its own.
  const selectRef = React.useRef<RefSelectProps>(null)
  React.useEffect(() => {
    selectRef.current?.nativeElement?.removeAttribute('aria-required')
  })

  return (
    <Field {...field}>
      {(control) => {
        // `checked` never applies to a select.
        const { value: boundValue, checked: _checked, onChange: commit, ...wiring } = control as FieldControlBag
        const isBound = 'onChange' in control

        return (
          <AntSelect
            ref={selectRef}
            mode={MODE_TO_ANT[mode]}
            size={SIZE_TO_ANT[size]}
            showSearch={searchable}
            optionFilterProp="label"
            allowClear={clearable}
            loading={loading}
            options={options}
            status={antStatus}
            {...rest}
            // Both run: a bound select still has call sites whose onChange drives page state.
            onChange={(next, option) => {
              commit?.(next)
              rest.onChange?.(next, option)
            }}
            {...(isBound ? { value: boundValue } : {})}
            {...wiring}
            style={style}
          />
        )
      }}
    </Field>
  )
}

export default SelectField
